import { useEffect, useState, useMemo } from "react";
import { api, pct, rollingMonthLabels } from "../api";
import ExportBar from "../components/ExportBar";

function cellColor(v) {
  if (v == null || v === 0) return "bg-slate-100 text-slate-400";
  if (v > 1) return "bg-red-100 text-red-800";
  if (v < 0.75) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

export default function HeatMap() {
  const [data, setData] = useState(null);
  const [portfolio, setPortfolio] = useState("");
  const [m1, m2, m3] = rollingMonthLabels(3);

  useEffect(() => { api.get("/data/latest").then((r) => setData(r.data)); }, []);

  const portfolioOptions = useMemo(
    () => Array.from(new Set((data?.employees || []).map((e) => e.portfolio || "Unassigned"))).sort(),
    [data]
  );

  const rows = useMemo(() => {
    const all = (data?.employees || []).slice().sort((a, b) => b.avg - a.avg);
    if (!portfolio) return all;
    return all.filter((e) => (e.portfolio || "Unassigned") === portfolio);
  }, [data, portfolio]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.empty) return <div className="bg-white p-8 rounded-xl text-center text-slate-500">No data.</div>;

  return (
    <div data-testid="heatmap-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>
            Utilization Heat Map <span className="text-xs font-normal text-slate-500">({rows.length} of {data.employees.length})</span>
          </h2>
          <select value={portfolio} onChange={(e) => setPortfolio(e.target.value)}
                  data-testid="hm-filter-portfolio"
                  className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white">
            <option value="">All Portfolios</option>
            {portfolioOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {portfolio && (
            <button onClick={() => setPortfolio("")} data-testid="hm-filter-clear"
                    className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
          )}
        </div>
        <ExportBar spec={() => ({
          title: "Heat Map",
          subtitle: portfolio ? `Portfolio: ${portfolio}` : undefined,
          filters: { portfolio: portfolio || "" },
          columns: [
            { key: "name", label: "Employee" },
            { key: "portfolio", label: "Portfolio" },
            { key: "cc", label: "CC" },
            { key: "apr", label: `${m1} %`, get: (r) => pct(r.apr) },
            { key: "may", label: `${m2} %`, get: (r) => pct(r.may) },
            { key: "jun", label: `${m3} %`, get: (r) => pct(r.jun) },
            { key: "avg", label: "Avg %", get: (r) => pct(r.avg) },
          ],
          rows,
          filename: portfolio ? `vSDI_HeatMap_${portfolio.replace(/\W+/g, "_")}` : "vSDI_HeatMap",
        })} />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Employee</th>
                <th className="px-3 py-2 text-left">Portfolio</th>
                <th className="px-3 py-2 text-center w-20">{m1}</th>
                <th className="px-3 py-2 text-center w-20">{m2}</th>
                <th className="px-3 py-2 text-center w-20">{m3}</th>
                <th className="px-3 py-2 text-center w-20">Avg</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={`${e.name}-${e.cc}`} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-medium">{e.name}</td>
                  <td className="px-3 py-1.5 text-slate-600 text-xs">{e.portfolio}</td>
                  {["apr", "may", "jun", "avg"].map((m) => (
                    <td key={m} className="px-1.5 py-1">
                      <div className={`text-center rounded px-2 py-1.5 font-semibold text-xs ${cellColor(e[m])}`}>{pct(e[m])}</div>
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No employees match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
